import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";
// import { DatePicker } from "@material-ui/pickers";
import { makeStyles } from "@material-ui/core/styles";
import {
  GetApp as DownloadIcon,
  Payment as PaymentIcon,
} from "@material-ui/icons";

const useStyles = makeStyles(theme => ({
  section: {
    padding: "16px",
    paddingTop: "8px",
    paddingLeft: "0",
    paddingRight: "16px",
    maxHeight: "100%",
    overflowY: "auto",
    maxWidth: "100%",
    [theme.breakpoints.down("sm")]: {
      padding: "15px",
      backgroundColor: "#1a1a1a",
      paddingBottom: "86px",
      height: "calc(100vh - 80px)",
      paddingRight: "15px",
    },
  },
  sectionTitle: {
    color: "#f06ec4",
    fontWeight: 600,
    fontSize: "24.5px",
    marginBottom: "3px",
  },
  infoText: {
    color: "white",
    fontSize: "11px",
    marginBottom: "30px",
  },
  summaryCard: {
    backgroundColor: "#1E1E1E",
    borderRadius: "8px",
    marginBottom: "20px",
    border: "1px solid #363636",
  },
  cardContent: {
    padding: "20px !important",
  },
  summaryItem: {
    textAlign: "center",
    padding: "16px",
    [theme.breakpoints.down("sm")]: {
      padding: "12px 8px",
    },
  },
  summaryValue: {
    color: "#f06ec4",
    fontSize: "24px",
    fontWeight: 600,
    marginBottom: "4px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
    },
  },
  summaryLabel: {
    color: "#ccc",
    fontSize: "12px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "11px",
    },
  },
  filtersContainer: {
    backgroundColor: "#1E1E1E",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "20px",
    border: "1px solid #363636",
  },
  filterRow: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    marginBottom: "16px",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "12px",
    },
  },
  filterField: {
    minWidth: "150px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "auto",
      width: "100%",
    },
    "& .MuiInputBase-root": {
      color: "white",
    },
    "& .MuiSelect-icon": {
      color: "#f06ec4",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EDECED",
      },
      "&:hover fieldset": {
        borderColor: "#f06ec4",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f06ec4",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#ccc",
    },
  },
  tableContainer: {
    backgroundColor: "#1E1E1E",
    borderRadius: "8px",
    border: "1px solid #363636",
    overflowX: "auto",
    [theme.breakpoints.down("sm")]: {
      borderRadius: "4px",
    },
    "& .MuiTableCell-root": {
      borderBottom: "1px solid #363636",
      color: "white",
      [theme.breakpoints.down("sm")]: {
        padding: "8px 4px",
        fontSize: "12px",
      },
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      backgroundColor: "#2a2a2a",
      color: "#f06ec4",
      fontWeight: 600,
      [theme.breakpoints.down("sm")]: {
        fontSize: "11px",
        padding: "8px 4px",
      },
    },
  },
  statusChip: {
    fontSize: "11px",
    height: "24px",
  },
  paidChip: {
    backgroundColor: "#4CAF50",
    color: "white",
  },
  pendingChip: {
    backgroundColor: "#FF9800",
    color: "white",
  },
  processingChip: {
    backgroundColor: "#2196F3",
    color: "white",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: "8px",
    },
  },
  requestPayoutButton: {
    backgroundColor: "#f06ec4",
    color: "white",
    "&:hover": {
      backgroundColor: "#d45ca5",
    },
  },
  exportButton: {
    backgroundColor: "transparent",
    color: "#f06ec4",
    border: "1px solid #f06ec4",
    "&:hover": {
      backgroundColor: "rgba(240, 110, 196, 0.1)",
    },
  },
  paymentInfoCard: {
    backgroundColor: "#2a2a2a",
    borderRadius: "8px",
    padding: "16px",
    marginTop: "20px",
  },
  paymentInfoTitle: {
    color: "#f06ec4",
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "12px",
  },
  paymentInfoText: {
    color: "#ccc",
    fontSize: "12px",
    marginBottom: "8px",
  },
}));

interface Transaction {
  id: string;
  date: string;
  type: "subscription" | "tip" | "pay_per_view" | "custom_content";
  amount: number;
  status: "paid" | "pending" | "processing";
  description: string;
}

const EarningsHistory: React.FC = () => {
  const classes = useStyles();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<
    Transaction[]
  >([]);
  const [filters, setFilters] = useState({
    dateFrom: null as Date | null,
    dateTo: null as Date | null,
    type: "all",
    status: "all",
  });

  // Mock data
  useEffect(() => {
    const mockTransactions: Transaction[] = [
      {
        id: "1",
        date: "2024-01-15",
        type: "subscription",
        amount: 9.99,
        status: "paid",
        description: "Monthly subscription - Basic",
      },
      {
        id: "2",
        date: "2024-01-14",
        type: "tip",
        amount: 25.0,
        status: "paid",
        description: "Tip from user @user123",
      },
      {
        id: "3",
        date: "2024-01-13",
        type: "pay_per_view",
        amount: 4.99,
        status: "pending",
        description: "Payment for video content",
      },
      {
        id: "4",
        date: "2024-01-12",
        type: "custom_content",
        amount: 50.0,
        status: "processing",
        description: "Personal custom video",
      },
    ];
    setTransactions(mockTransactions);
    setFilteredTransactions(mockTransactions);
  }, []);

  const totalEarnings = transactions.reduce((sum, t) => sum + t.amount, 0);
  const availableForPayout = transactions
    .filter(t => t.status === "paid")
    .reduce((sum, t) => sum + t.amount, 0);
  const pendingEarnings = transactions
    .filter(t => t.status === "pending" || t.status === "processing")
    .reduce((sum, t) => sum + t.amount, 0);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "subscription":
        return "Subscription";
      case "tip":
        return "Tip";
      case "pay_per_view":
        return "Pay-per-view";
      case "custom_content":
        return "Custom content";
      default:
        return type;
    }
  };

  const getStatusChip = (status: string) => {
    const chipClasses = [classes.statusChip];
    let label = "";

    switch (status) {
      case "paid":
        chipClasses.push(classes.paidChip);
        label = "Paid";
        break;
      case "pending":
        chipClasses.push(classes.pendingChip);
        label = "Pending";
        break;
      case "processing":
        chipClasses.push(classes.processingChip);
        label = "Processing";
        break;
    }

    return <Chip label={label} className={chipClasses.join(" ")} />;
  };

  const handleRequestPayout = () => {
    // TODO: Implement payout request API call
  };

  const handleExportData = () => {
    // TODO: Implement data export functionality
  };

  return (
    <div className={classes.section}>
      <Typography className={classes.sectionTitle}>
        Earnings History & Payouts
      </Typography>
      <Typography className={classes.infoText}>
        Track your earnings, view transaction history, and manage payouts.
      </Typography>

      {/* Summary Cards */}
      <Card className={classes.summaryCard}>
        <CardContent className={classes.cardContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box className={classes.summaryItem}>
                <Typography className={classes.summaryValue}>
                  ${totalEarnings.toFixed(2)}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Total Earnings
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box className={classes.summaryItem}>
                <Typography className={classes.summaryValue}>
                  ${availableForPayout.toFixed(2)}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Available for Payout
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box className={classes.summaryItem}>
                <Typography className={classes.summaryValue}>
                  ${pendingEarnings.toFixed(2)}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Processing
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className={classes.filtersContainer}>
        <Typography
          style={{
            color: "#f06ec4",
            fontSize: "16px",
            fontWeight: 600,
            marginBottom: "16px",
          }}
        >
          Filters
        </Typography>
        <Box className={classes.filterRow}>
          <FormControl
            className={classes.filterField}
            variant="outlined"
            size="small"
          >
            <Select
              value={filters.type}
              onChange={e =>
                setFilters({ ...filters, type: e.target.value as string })
              }
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    backgroundColor: "#1E1E1E",
                    color: "white",
                  },
                },
              }}
            >
              <MenuItem value="all" style={{ color: "white" }}>
                All Types
              </MenuItem>
              <MenuItem value="subscription" style={{ color: "white" }}>
                Subscriptions
              </MenuItem>
              <MenuItem value="tip" style={{ color: "white" }}>
                Tips
              </MenuItem>
              <MenuItem value="pay_per_view" style={{ color: "white" }}>
                Pay-per-view
              </MenuItem>
              <MenuItem value="custom_content" style={{ color: "white" }}>
                Custom Content
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl
            className={classes.filterField}
            variant="outlined"
            size="small"
          >
            <Select
              value={filters.status}
              onChange={e =>
                setFilters({ ...filters, status: e.target.value as string })
              }
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    backgroundColor: "#1E1E1E",
                    color: "white",
                  },
                },
              }}
            >
              <MenuItem value="all" style={{ color: "white" }}>
                All Statuses
              </MenuItem>
              <MenuItem value="paid" style={{ color: "white" }}>
                Paid
              </MenuItem>
              <MenuItem value="pending" style={{ color: "white" }}>
                Pending
              </MenuItem>
              <MenuItem value="processing" style={{ color: "white" }}>
                Processing
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Transactions Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.map(transaction => (
              <TableRow key={transaction.id}>
                <TableCell>
                  {new Date(transaction.date).toLocaleDateString("uk-UA")}
                </TableCell>
                <TableCell>{getTypeLabel(transaction.type)}</TableCell>
                <TableCell>{transaction.description}</TableCell>
                <TableCell align="right">
                  ${transaction.amount.toFixed(2)}
                </TableCell>
                <TableCell align="center">
                  {getStatusChip(transaction.status)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Buttons */}
      <Box className={classes.actionButtons}>
        <Button
          className={classes.requestPayoutButton}
          startIcon={<PaymentIcon />}
          onClick={handleRequestPayout}
          disabled={availableForPayout === 0}
        >
          Request Payout (${availableForPayout.toFixed(2)})
        </Button>
        <Button
          className={classes.exportButton}
          startIcon={<DownloadIcon />}
          onClick={handleExportData}
        >
          Export Data
        </Button>
      </Box>

      {/* Payment Information */}
      <Card className={classes.paymentInfoCard}>
        <Typography className={classes.paymentInfoTitle}>
          Payout Information
        </Typography>
        <Typography className={classes.paymentInfoText}>
          • Payouts are processed within 3-5 business days
        </Typography>
        <Typography className={classes.paymentInfoText}>
          • Minimum payout amount: $50.00
        </Typography>
        <Typography className={classes.paymentInfoText}>
          • Platform fee: 20% of transaction amount
        </Typography>
        <Typography className={classes.paymentInfoText}>
          • To set up payment information, go to "Billing" section
        </Typography>
      </Card>
    </div>
  );
};

export default EarningsHistory;
